import type { ReactNode } from 'react';
import React, { createContext, Fragment, useState } from 'react';

import { useMutation } from '@apollo/client';
import { FieldArray, useFormik } from 'formik';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import autoCompleteStyles from '../components/User/Forms/autocompleteStyles';
import { ADD_USER_PROFESSION } from '../graphql/ADD_USER_PROFESSION';
import { DELETE_USER_PROFESSION } from '../graphql/DELETE_USER_PROFESSION';
import { GET_PROFESSIONS_BY_FILTER_QUERY } from '../graphql/GET_PROFESSIONS_BY_FILTER_QUERY';
import { GET_CURRENT_USER_me_professions } from '../graphql/types/GET_CURRENT_USER';
import { IHandleOptionSelect, useAutocomplete } from '../hooks/useAutocomplete';
import searchIcon from '../assets/svg/search.svg';

const initData = {
  professionNames: [] as string[]
};

export const UserProfessionContext = createContext(initData);

interface IUserProfessionContextProviderProps {
  children: ReactNode;
  error?: string;
  professions: GET_CURRENT_USER_me_professions[];
  hideTitle?: boolean;
}

const userProfessionSchema = Yup.object().shape({
  newProfession: Yup.string().required('This field is required.')
});

export const UserProfessionContextProvider = (
  props: IUserProfessionContextProviderProps
) => {
  const [addUserProfession] = useMutation(ADD_USER_PROFESSION, {
    onCompleted: () => toast.success('User profession successfully added.')
  });

  const [deleteUserProfession] = useMutation(DELETE_USER_PROFESSION, {
    onCompleted: () => toast.success('User profession successfully deleted.')
  });

  const professionsOptsSelectCb = async (params: IHandleOptionSelect) => {
    if (!params) {
      return;
    }

    const { __isNew__, label, value } = params;

    await addUserProfession({
      variables: { professionName: __isNew__ ? value : label }
    });
  };

  const { loadOptions, loading } = useAutocomplete({
    query: GET_PROFESSIONS_BY_FILTER_QUERY
  });

  const { handleSubmit, values } = useFormik({
    enableReinitialize: true,
    initialValues: {
      newProfession: '',
      professionNames: props.professions.map((prof) => prof.name) || []
    },
    onSubmit: async (_, { setSubmitting }) => {
      setSubmitting(false);
    },
    validationSchema: userProfessionSchema
  });

  const [inputValue, setInputValue] = useState('');

  return (
    <UserProfessionContext.Provider value={values}>
      <form onSubmit={handleSubmit} className="m-b-1">
        <FieldArray name="professionNames">
          {(arrayHelper) => {
            return (
              <Fragment>
                <div className="profession-selection-container">
                  <div className="profession-search-wrapper">
                    <img
                      src={searchIcon}
                      alt=""
                      className={`search-icon ${inputValue ? 'is-hidden' : ''}`}
                    />

                    <div className="profession-input-container">
                      <AsyncCreatableSelect
                        cacheOptions={true}
                        isClearable={true}
                        className="profession-async-select"
                        classNamePrefix="react-select"
                        escapeClearsValue={true}
                        backspaceRemovesValue={true}
                        isLoading={loading}
                        onChange={professionsOptsSelectCb}
                        onInputChange={(value) => setInputValue(value)}
                        loadOptions={loadOptions}
                        placeholder="Search to add"
                        styles={autoCompleteStyles}
                        components={{
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null
                        }}
                        value={null}
                      />
                      {values.professionNames && values.professionNames.length > 0 && (
                        <div className="selected-professions-container">
                          {values.professionNames.map((name, idx) => (
                            <div key={`${idx}-${name}`} className="profession-tag">
                              <span className="profession-name">{name}</span>
                              <button
                                type="button"
                                className="remove-button"
                                onClick={async () => {
                                  await deleteUserProfession({
                                    variables: { professionName: name }
                                  });
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {props.error && (
                  <span className="error field-error has-text-danger is-uppercase">
                    {props.error}
                  </span>
                )}
              </Fragment>
            );
          }}
        </FieldArray>
        {props.children}
      </form>
    </UserProfessionContext.Provider>
  );
};
